
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Star } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { handleError } from "@/utils/errorHandler";
import FeedbackDisplay from "@/components/feedback/FeedbackDisplay";
import LoadingSpinner from "@/components/common/LoadingSpinner";

interface EvaluationsTabProps {
  psychologistId?: string;
}

const EvaluationsTab = ({ psychologistId }: EvaluationsTabProps) => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (psychologistId) {
      loadStats();
    }
  }, [psychologistId]);

  const loadStats = async () => {
    if (!psychologistId) return;

    setLoading(true);
    setError(null);

    try {
      // Carregar estatísticas básicas de feedback
      const { data, error } = await supabase
        .from('feedback')
        .select('rating, created_at')
        .eq('psychologist_id', psychologistId);

      if (error) throw error;

      // Calcular estatísticas simples
      const totalReviews = data?.length || 0;
      const averageRating = totalReviews > 0 
        ? data.reduce((sum, item) => sum + item.rating, 0) / totalReviews 
        : 0;

      setStats({
        total_reviews: totalReviews,
        average_rating: averageRating,
        five_star: data?.filter(f => f.rating === 5).length || 0,
        four_star: data?.filter(f => f.rating === 4).length || 0,
        three_star: data?.filter(f => f.rating === 3).length || 0,
        two_star: data?.filter(f => f.rating === 2).length || 0,
        one_star: data?.filter(f => f.rating === 1).length || 0,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
      setError('Erro ao carregar estatísticas. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const exportEvaluations = async () => {
    if (!psychologistId) return;

    try {
      // Buscar feedback
      const { data: feedbackData, error: feedbackError } = await supabase
        .from('feedback')
        .select('*')
        .eq('psychologist_id', psychologistId)
        .order('created_at', { ascending: false });

      if (feedbackError) throw feedbackError;

      // Buscar perfis dos usuários
      const userIds = feedbackData?.map(f => f.user_id).filter(Boolean) || [];
      
      let profiles: any[] = [];
      if (userIds.length > 0) {
        const { data } = await supabase
          .from('profiles')
          .select('id, first_name, last_name')
          .in('id', userIds);
        profiles = data || [];
      }

      const profilesMap = new Map(profiles.map(p => [p.id, p]));

      const headers = ['Data', 'Usuário', 'Nota', 'Comentário', 'Anônimo'];
      const csvContent = [
        headers.join(','),
        ...(feedbackData || []).map(feedback => {
          const profile = profilesMap.get(feedback.user_id);
          const userName = feedback.is_anonymous 
            ? 'Usuário Anônimo' 
            : profile 
              ? `${profile.first_name || 'Usuário'} ${profile.last_name || ''}`
              : 'Usuário';
          
          return [
            new Date(feedback.created_at).toLocaleDateString('pt-BR'),
            `"${userName}"`,
            feedback.rating,
            `"${feedback.comment || ''}"`,
            feedback.is_anonymous ? 'Sim' : 'Não'
          ].join(',');
        })
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `avaliacoes_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Relatório exportado com sucesso!');
    } catch (error) {
      console.error('Error exporting evaluations:', error);
      toast.error('Erro ao exportar avaliações');
    }
  };

  if (loading) {
    return <LoadingSpinner size="lg" text="Carregando avaliações..." />;
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p className="text-red-600">{error}</p>
          <Button 
            onClick={loadStats} 
            variant="outline" 
            className="mt-2"
          >
            Tentar Novamente
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Card */}
      {stats && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Star className="h-5 w-5" />
                Estatísticas de Avaliações
              </span>
              <Button 
                onClick={exportEvaluations} 
                variant="outline"
                disabled={stats.total_reviews === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                Exportar CSV
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary">{stats.total_reviews}</div>
                <div className="text-sm text-muted-foreground">Total de Avaliações</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-500">
                  {stats.average_rating.toFixed(1)}
                </div>
                <div className="text-sm text-muted-foreground">Nota Média</div>
              </div>
              <div className="space-y-2">
                <div className="text-sm font-medium">Distribuição:</div>
                {[5, 4, 3, 2, 1].map(star => {
                  const count = stats[`${star === 1 ? 'one' : star === 2 ? 'two' : star === 3 ? 'three' : star === 4 ? 'four' : 'five'}_star`] || 0;
                  const percentage = stats.total_reviews > 0 ? (count / stats.total_reviews) * 100 : 0;
                  return (
                    <div key={star} className="flex items-center gap-2 text-sm">
                      <span>{star}⭐</span>
                      <div className="flex-1 bg-muted rounded-full h-2">
                        <div 
                          className="bg-yellow-500 h-2 rounded-full" 
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="w-8 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Feedback Display Component */}
      {psychologistId && (
        <FeedbackDisplay 
          psychologistId={psychologistId}
          limit={20}
          showFilters={true}
        />
      )}
    </div>
  );
};

export default EvaluationsTab;
